import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, MouseEvent } from 'react';
import { cn } from '../../lib/cn.ts';
import { buttonTapped } from '../../lib/usage.ts';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-[background-color,color,box-shadow,transform] outline-none active:translate-y-px focus-visible:ring-4 focus-visible:ring-brand/25 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-brand text-brand-foreground shadow-[0_8px_18px_-10px_var(--brand)] hover:bg-[color-mix(in_oklab,var(--brand)_88%,black)]',
        secondary: 'bg-paper text-ink hover:bg-line',
        outline: 'bg-surface text-ink shadow-[inset_0_0_0_1px_var(--line)] hover:bg-paper',
        destructive: 'bg-danger text-white hover:bg-[color-mix(in_oklab,var(--danger)_88%,black)] focus-visible:ring-danger/25',
        ghost: 'text-ink hover:bg-paper',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-5 text-[0.92rem]',
        sm: 'h-9 px-4 text-[0.82rem]',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** The button's fixed id in the usage analytics (lib/usage.ts); a press is counted when it has one. */
  track?: string;
}

export function Button({ className, variant, size, asChild = false, type = 'button', track, onClick, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      type={asChild ? undefined : type}
      onClick={
        track
          ? (event: MouseEvent<HTMLButtonElement>) => {
              buttonTapped(track);
              onClick?.(event);
            }
          : onClick
      }
      {...props}
    />
  );
}
